import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/OidcProvider";
import { useTenant } from "@/tenants";
import { generateClientConversationId } from "../chat/api";
import { DISABLED_CAPABILITIES } from "../chat/attachments/types";
import { fetchAgentsInfo } from "@/pages/chat/agentsInfo";
import {
  COURSE_ASSISTANCE_MODES,
  KNOWLEDGE_SOURCES,
  TEACHER_NOTES,
} from "./workbenchData";
import {
  buildTeacherRunContext,
  filterNotesForContext,
} from "./workbenchModel";
import { ConversationsPanel } from "./ConversationsPanel";
import { EmployeeSwitcher, prefsToContextNote, useMyEmployees } from "./EmployeeSwitcher";
import { SolutionChatWorkspace } from "./SolutionChatWorkspace";
import { RightRail } from "./RightRail";
import { ClassSubjectPicker } from "./ClassSubjectPicker";
import { ModePicker } from "./ModePicker";
import { useClasses } from "@/api/school";
import {
  createDraftConversationSelection,
  selectExistingConversation,
  shouldLoadConversationHistory,
} from "./conversationSelection";
import {
  getProgressFor,
  getRunningConversationIds,
  progressSetConversation,
  subscribeProgress,
} from "../chat/progressStore";
import type { CourseAssistanceMode } from "./types";
import "./workbench.css";

// Stable empty selectedNoteIds reference shared across every render so
// the value never changes identity. Without this the array literal
// would cascade through useMemo deps (contextNote → runContext →
// agent → useAgUiRuntime) and reinitialise the AG-UI runtime per
// render, which made every reply spawn a new backend conversation.
const EMPTY_NOTE_IDS: string[] = Object.freeze([] as string[]) as string[];

// Keep-alive snapshot (#28). A workspace whose conversation still has a
// live run is kept mounted (hidden) with the props it was opened with —
// subject/runContext are frozen so the instance's identity key never
// changes under it (picker changes only apply to the active workspace).
interface KeptWorkspace {
  convId: string;
  subject: { agentId: string; labelKey: string; label?: string; isFallback: boolean };
  runContext: Record<string, unknown>;
  loadHistory: boolean;
}

export function TeacherWorkbenchPage(): JSX.Element {
  const { state, authHeader } = useAuth();
  const tenant = useTenant();
  const { t } = useTranslation();

  // Seed initial subject + conversation from ?conversation=&subject= so
  // Sidebar Recents URLs (and the chat-landing's "Tutor chat → Open"
  // link) jump straight to the matching thread. Subsequent internal
  // subject/conversation changes clear the query (handleSubjectChange,
  // handleNewConversation, handleConversationSelect below).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlConversation = searchParams.get("conversation");

  const [modeId, setModeId] =
    useState<CourseAssistanceMode["id"]>("solution_search");
  // The notes panel went away when chat history moved into the left
  // column; we still synthesize runContext.selected_private_note_ids as
  // [] to preserve the agent contract. If a future iteration brings
  // notes back as a secondary tab, this becomes useState again.
  //
  // CRITICAL: this MUST be a stable reference. Using a fresh array
  // literal per render cascades through useMemo dep arrays
  // (contextNote → runContext → agent → useAgUiRuntime), reinitialising
  // the AG-UI runtime on every render and creating a NEW conversation
  // on the backend each turn. Bug surfaced in v25 — recents grew an
  // extra row after every reply.
  const selectedNoteIds = EMPTY_NOTE_IDS;
  const [conversationSelection, setConversationSelection] = useState(() =>
    urlConversation
      ? selectExistingConversation(urlConversation)
      : createDraftConversationSelection(generateClientConversationId),
  );
  const [hasFirstRun, setHasFirstRun] = useState(false);
  // 右栏(动态/产物)收起状态。聊天列本来就窄,右栏常驻会把它挤得更窄;
  // 左侧栏早有「收起侧栏」,右栏一直没有(Jarod 2026-09-02)。按浏览器记住。
  const [railOpen, setRailOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("teacher-rail-open") !== "0";
    } catch {
      return true; // 隐私模式/禁用存储时不该影响功能
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("teacher-rail-open", railOpen ? "1" : "0");
    } catch {
      /* 存不了就算了,只是记不住偏好 */
    }
  }, [railOpen]);
  const handleFirstMessage = useCallback(() => {
    setHasFirstRun(true);
  }, []);

  // ── In-flight run keep-alive (#28) ────────────────────────────────
  // Switching conversations used to remount SolutionChatWorkspace (keyed
  // on conversationId); unmount aborts the AG-UI fetch and the server
  // cancels the run mid-synthesis (observed live: "persona synthesis
  // call: context canceled"). Instead: when the user switches away from
  // a conversation whose run is still live, keep that workspace mounted
  // but hidden until the run finishes. Coming back shows the live
  // thread. Leaving the chat page entirely still unmounts everything —
  // known limitation, tracked on the issue.
  const runningConvIds = useSyncExternalStore(
    subscribeProgress,
    getRunningConversationIds,
    getRunningConversationIds,
  );
  const [keptWorkspaces, setKeptWorkspaces] = useState<KeptWorkspace[]>([]);
  // Per-conversation snapshot of the props the active workspace is
  // currently rendered with — read back by the switch-away effect (which
  // fires after the NEW conversation already rendered).
  const snapshotsRef = useRef(new Map<string, KeptWorkspace>());
  const prevConvIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevConvIdRef.current;
    prevConvIdRef.current = conversationSelection.id;
    if (!prev || prev === conversationSelection.id) return;
    if (!getProgressFor(prev).running) return;
    const snap = snapshotsRef.current.get(prev);
    if (!snap) return;
    setKeptWorkspaces((k) =>
      k.some((w) => w.convId === prev) ? k : [...k, snap],
    );
  }, [conversationSelection.id]);

  // Drop a kept workspace once its run finished — the conversation is
  // persisted server-side by then, so a fresh mount (with history load)
  // takes over if the user is looking at it.
  useEffect(() => {
    setKeptWorkspaces((k) => {
      const next = k.filter((w) => runningConvIds.includes(w.convId));
      return next.length === k.length ? k : next;
    });
  }, [runningConvIds]);

  // The 动态 panel follows the viewed conversation. The agent constructor
  // also sets this, but returning to a kept-alive workspace constructs no
  // new agent — the page owns the pointer.
  useEffect(() => {
    progressSetConversation(conversationSelection.id ?? "");
  }, [conversationSelection.id]);

  // Sync URL → state when the user clicks a sidebar Recents row while
  // already on /. The useState initializers above only fire on mount;
  // without this effect, in-page navigation via ?conversation=&subject=
  // would update the URL but not the displayed conversation.
  useEffect(() => {
    if (urlConversation) {
      setConversationSelection((prev) =>
        prev.id === urlConversation
          ? prev
          : selectExistingConversation(urlConversation),
      );
    }
  }, [urlConversation]);

  const { data: agentsInfo } = useQuery({
    queryKey: ["copilotkit-agents-info", "teacher-workbench"],
    queryFn: () => fetchAgentsInfo(authHeader),
    enabled: state.status === "authenticated",
    staleTime: 60_000,
  });

  const availableAgentIds = agentsInfo?.agentIds ?? [];
  // Resolve the effective agent: stored pick → tenant.subjects →
  // nova-orchestrator (per Jarod's 2026-06-04 demo feedback) → first.
  // The previous `default` fallback was the generic catch-all that
  // returned underwhelming responses on demo runs; nova-orchestrator
  // is persona-aware and routes to richer behaviour by default.
  // M3: 学科 AI 助教。有已注册 employee 的老师默认绑定自己的学科助教
  // (单学科自动;多学科顶部切换);无学科/未注册回落 nova-orchestrator。
  const { employees } = useMyEmployees();
  const registeredEmployees = useMemo(
    () => employees.filter((e) => e.registered && availableAgentIds.includes(e.agent_id)),
    [employees, availableAgentIds],
  );
  const [pickedEmployeeAgent, setPickedEmployeeAgent] = useState<string | null>(() =>
    sessionStorage.getItem("nova_chat_employee") || null,
  );
  const effectiveAgentId = useMemo(() => {
    // 恢复上次选择时必须校验"这个助教属于当前老师" —— 只查
    // availableAgentIds(全校已注册助教)会让上一个账号/上一次身份
    // 残留的选择存活下来:数学物理老师顶着 school-employee-chemistry,
    // 且因为 activeEmployee 匹配不上,标题回退成原始 agent id
    // (Jarod 2026-08-27)。
    const mine = registeredEmployees.map((e) => e.agent_id);
    if (pickedEmployeeAgent && mine.includes(pickedEmployeeAgent)) {
      return pickedEmployeeAgent;
    }
    if (registeredEmployees.length > 0) return registeredEmployees[0]!.agent_id;
    if (availableAgentIds.includes("nova-orchestrator")) return "nova-orchestrator";
    const firstNonDefault = availableAgentIds.find((id) => id !== "default");
    return firstNonDefault ?? availableAgentIds[0] ?? "";
  }, [pickedEmployeeAgent, registeredEmployees, availableAgentIds]);
  const activeEmployee = useMemo(
    () => employees.find((e) => e.agent_id === effectiveAgentId),
    [employees, effectiveAgentId],
  );

  // Class + subject context — what the teacher is asking *about*, separate
  // from which agent is answering. Without it, picks like `default` have
  // no domain context and the LLM defaults to whatever feels likely (Bosong
  // bug: English teacher asked for reading practice, got 语文 because the
  // Chinese school context dominated the prior). Pick is persisted so it
  // survives in-app navigation.
  const [pickedClassId, setPickedClassId] = useState<string | null>(() =>
    sessionStorage.getItem("nova_chat_class") || null,
  );
  const handleClassChange = useCallback((next: string | null) => {
    setPickedClassId(next);
    if (next) sessionStorage.setItem("nova_chat_class", next);
    else sessionStorage.removeItem("nova_chat_class");
  }, []);
  const classes = useClasses();
  const pickedClass = useMemo(
    () => classes.data?.find((c) => c.id === pickedClassId) ?? null,
    [classes.data, pickedClassId],
  );

  // Synthesize the shape that SolutionChatWorkspace + AssistantUiThread
  // still expect (carrying a `subject` prop). Drop the fallback flag —
  // the picker only offers actually-registered agents now, so the chat
  // POST hits ?app=school directly without the unconditional drop.
  // The labelKey fallback to the agent id lets the topbar render
  // something readable even without an i18n entry.
  const subject = useMemo(() => {
    const matchingTenantSubject = tenant.subjects.find(
      (s) => s.agentId === effectiveAgentId,
    );
    // 学科助教显示友好名(与 agent frontmatter 的 {name}助教/助手 一致),
    // 不再把 school-employee-english 这类原始 id 怼在标题/占位符上。
    const label = activeEmployee
      ? `${activeEmployee.name}${activeEmployee.role_type === "functional" ? t("employees.assistantSuffixFunctional") : t("employees.assistantSuffix")}`
      : matchingTenantSubject
        ? String(t(matchingTenantSubject.labelKey))
        : String(t(`chat.agent.${effectiveAgentId}`, { defaultValue: effectiveAgentId }));
    return {
      agentId: effectiveAgentId,
      labelKey:
        matchingTenantSubject?.labelKey ?? `chat.agent.${effectiveAgentId}`,
      label,
      isFallback: false,
    };
  }, [effectiveAgentId, tenant.subjects, activeEmployee, t]);

  const selectedMode =
    COURSE_ASSISTANCE_MODES.find((mode) => mode.id === modeId) ??
    COURSE_ASSISTANCE_MODES[0];
  const subjectLabel = subject.agentId ? subject.label : "";

  const notes = useMemo(
    () =>
      subject
        ? filterNotesForContext(TEACHER_NOTES, {
            subjectAgentId: subject.agentId,
          })
        : [],
    [subject],
  );

  const sources = useMemo(
    () =>
      subject
        ? KNOWLEDGE_SOURCES.filter(
            (source) => source.subjectAgentId === subject.agentId,
          )
        : [],
    [subject],
  );

  const contextNote = useMemo(
    () =>
      notes.find((note) => selectedNoteIds.includes(note.id)) ?? notes[0],
    [notes, selectedNoteIds],
  );

  const runContext = useMemo<Record<string, unknown> | null>(
    () => {
      if (!subject || !selectedMode) return null;

      const note = prefsToContextNote(activeEmployee);
      return {
        ...(note ? { context_note: note } : {}),
        ...buildTeacherRunContext({
          subjectAgentId: subject.agentId,
          courseAssistanceId: selectedMode.id,
          selectedNoteIds,
          gradeBand: contextNote?.gradeBand ?? "All grades",
          course: contextNote?.course ?? subjectLabel,
          standard: contextNote?.standard ?? "Unspecified standard",
          schoolClassId: pickedClass?.id ?? null,
          schoolClassName: pickedClass?.name ?? null,
          schoolSubject: pickedClass?.subject ?? null,
        }),
      };
    },
    [
      contextNote?.course,
      contextNote?.gradeBand,
      contextNote?.standard,
      selectedMode,
      selectedNoteIds,
      subject,
      subjectLabel,
      pickedClass,
      activeEmployee,
    ],
  );

  if (state.status === "loading") {
    return (
      <div className="teacher-workbench teacher-workbench--state">
        Loading the teacher workbench...
      </div>
    );
  }

  if (state.status !== "authenticated") {
    return (
      <div className="teacher-workbench teacher-workbench--state">
        Sign in to use the teacher workbench.
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="teacher-workbench teacher-workbench--state">
        No subjects configured for this tenant.
      </div>
    );
  }

  if (!selectedMode) {
    return (
      <div className="teacher-workbench teacher-workbench--state">
        No course assistance modes configured.
      </div>
    );
  }

  if (!runContext) {
    return (
      <div className="teacher-workbench teacher-workbench--state">
        Preparing the teacher workbench context.
      </div>
    );
  }

  const teacherId = state.user.id;
  const attachmentCapabilities =
    agentsInfo?.attachments ?? DISABLED_CAPABILITIES;
  function clearUrlQuery(): void {
    if (searchParams.has("conversation") || searchParams.has("subject")) {
      setSearchParams({}, { replace: true });
    }
  }

  function handleNewConversation(): void {
    setHasFirstRun(false);
    setConversationSelection(
      createDraftConversationSelection(generateClientConversationId),
    );
    clearUrlQuery();
  }

  function handleConversationSelect(id: string, agentId?: string): void {
    // 会话属于哪个学科助教,就切到哪个 —— 最近对话是跨学科合并的,
    // 不跟着切的话会出现「English 助教顶着一段数学大纲对话」,后续提问
    // 也发给了错的助教(Jarod 2026-08-27)。只在该助教确实属于这位老师
    // 时切换,避免把别人的学科塞进来。
    if (agentId && agentId !== effectiveAgentId
        && registeredEmployees.some((e) => e.agent_id === agentId)) {
      setPickedEmployeeAgent(agentId);
      sessionStorage.setItem("nova_chat_employee", agentId);
    }
    setConversationSelection(selectExistingConversation(id));
    clearUrlQuery();
  }

  return (
    <div className="teacher-workbench">
      <div className="teacher-workbench__topbar">
        <div>
          <p className="teacher-eyebrow">{String(t("nav.chat"))}</p>
          <h1>{subjectLabel}</h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <EmployeeSwitcher
            employees={registeredEmployees}
            activeAgentId={effectiveAgentId}
            onPick={(id) => {
              setPickedEmployeeAgent(id);
              sessionStorage.setItem("nova_chat_employee", id);
            }}
          />
          <ClassSubjectPicker
            classId={pickedClassId}
            onChange={handleClassChange}
          />
          {/* 对话对象 (agent) picker hidden for the school deployment —
              nova-orchestrator is the fixed default (effectiveAgentId). */}
          <ModePicker
            modes={COURSE_ASSISTANCE_MODES}
            modeId={selectedMode.id}
            onModeChange={setModeId}
          />
        </div>
      </div>

      <div
        className={`relative teacher-grid ${hasFirstRun && railOpen ? "is-three-col" : "is-two-col"}`}
      >
        <ConversationsPanel
          activeConversationId={conversationSelection.id}
          onSelect={handleConversationSelect}
          onNewConversation={handleNewConversation}
        />

        {(() => {
          const activeId = conversationSelection.id;
          // Record the active workspace's prop snapshot so the
          // switch-away effect can keep this exact instance alive.
          snapshotsRef.current.set(activeId, {
            convId: activeId,
            subject,
            runContext,
            loadHistory: shouldLoadConversationHistory(conversationSelection),
          });
          const activeIsKept = keptWorkspaces.some(
            (w) => w.convId === activeId,
          );
          // Kept instances render from their frozen snapshots (stable
          // identity key); the active one renders live unless it IS a
          // kept instance (came back mid-run — reuse it, don't remount).
          const panes: Array<{ w: KeptWorkspace; live: boolean }> = [
            ...keptWorkspaces.map((w) => ({ w, live: false })),
            ...(activeIsKept
              ? []
              : [{ w: snapshotsRef.current.get(activeId)!, live: true }]),
          ];
          return (
            <div className="teacher-chat-slot">
              {panes.map(({ w, live }) => (
                <div
                  key={w.convId}
                  className={`teacher-chat-slot__pane${
                    w.convId === activeId ? " is-active" : ""
                  }`}
                >
                  <SolutionChatWorkspace
                    key={`${teacherId}-${w.subject.agentId}-${w.convId}`}
                    subject={live ? subject : w.subject}
                    /* Placeholder shape — AssistantUiThread accepts these
                       props for backward compat but doesn't use them
                       (prefixed with `_` internally). Real agent selection
                       happens upstream via AgentPicker → effectiveAgentId
                       → subject.agentId. */
                    subjects={[
                      {
                        agentId: (live ? subject : w.subject).agentId,
                        labelKey: (live ? subject : w.subject).labelKey,
                      },
                    ]}
                    subjectIdx={0}
                    onSubjectChange={() => {}}
                    teacherId={teacherId}
                    authHeader={authHeader}
                    conversationId={w.convId}
                    loadConversationHistory={w.loadHistory}
                    attachmentCapabilities={attachmentCapabilities}
                    runContext={live ? runContext : w.runContext}
                    onNewConversation={handleNewConversation}
                    onSelectConversation={handleConversationSelect}
                    onFirstMessage={handleFirstMessage}
                  />
                </div>
              ))}
            </div>
          );
        })()}

        {hasFirstRun &&
          (railOpen ? (
            <RightRail sources={sources} onCollapse={() => setRailOpen(false)} />
          ) : (
            // 收起后不能再当栅格子项渲染 —— 两列布局下它会换行掉到第一列
            // 底下(Jarod 2026-09-02)。改为贴着栅格右上角绝对定位。
            <button
              type="button"
              onClick={() => setRailOpen(true)}
              title={String(t("workbench.tabs.expandRail"))}
              aria-label={String(t("workbench.tabs.expandRail"))}
              className="absolute right-0 top-0 z-10 rounded-l border border-r-0 border-slate-200 bg-white px-1.5 py-2 text-slate-400 shadow-sm hover:text-slate-600"
            >
              ‹
            </button>
          ))}
      </div>
    </div>
  );
}

export default TeacherWorkbenchPage;
